import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  TestsResultsAnalyticsClient_testsResultsAnalyticsClient_tests,
  getResultLabelColor,
  SortByType,
  getFormattedDateAndTime,
  TestType,
  TestSessionPaymentStatus,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Label from 'components/Label'
import Card from 'components/Card'
import { isInfluenza } from 'utils/helpers'

interface ColumnLabels {
  patient?: string | null
  barcode?: string | null
  result?: string | null
  testCategory?: string | null
  testSessionName?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  tests: TestsResultsAnalyticsClient_testsResultsAnalyticsClient_tests[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: TestsResultsAnalyticsClient_testsResultsAnalyticsClient_tests) => void
}

const defaultColumnLabels = {
  patient: 'Patient',
  insuranceCompany: 'Insurance company',
  barcode: 'Barcode',
  result: 'Result',
  testCategory: 'Test category',
  testSessionName: 'Test Session name',
}

const PAGE_LENGTH = 10

export const AnalyticsTestsResultsClientDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  tests,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={tests}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.patient) ? (
                <Datalist.HeaderCell label={columnLabels.patient} />
              ) : null}
              {!isNull(columnLabels.insuranceCompany) ? (
                <Datalist.HeaderCell label={columnLabels.insuranceCompany} />
              ) : null}
              {!isNull(columnLabels.barcode) ? (
                <Datalist.HeaderCell label={columnLabels.barcode} />
              ) : null}
              {!isNull(columnLabels.result) ? (
                <Datalist.HeaderCell sortKey={SortByType.RESULT} label={columnLabels.result} />
              ) : null}
              {!isNull(columnLabels.testCategory) ? (
                <Datalist.HeaderCell label={columnLabels.testCategory} />
              ) : null}
              {!isNull(columnLabels.testSessionName) ? (
                <Datalist.HeaderCell label={columnLabels.testSessionName} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { barcode, result, TestSession, User, secondResult, Insurance } = data
            const participant = User
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.patient) ? (
                  <Datalist.Cell label={columnLabels.patient}>
                    {participant && (
                      <>
                        {participant.firstName} {participant.lastName} (
                        {getFormattedDateAndTime(participant.birthDate)})
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.insuranceCompany) ? (
                  <Datalist.Cell label={columnLabels.insuranceCompany}>
                    {Insurance &&
                    Insurance.insuranceCompany &&
                    TestSession.paymentStatus == TestSessionPaymentStatus.Insurance
                      ? Insurance.insuranceCompany
                      : ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.barcode) ? (
                  <Datalist.Cell label={columnLabels.barcode}>{barcode}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.result) ? (
                  <Datalist.Cell label={columnLabels.result}>
                    <Label indicator={result ? getResultLabelColor(result) : 'blue'}>
                      {isInfluenza(TestSession.type as TestType) && `A: `}
                      {result || 'Pending result'}
                    </Label>
                    {isInfluenza(TestSession.type as TestType) && secondResult && (
                      <Label indicator={getResultLabelColor(secondResult)}>
                        {`B: ${secondResult}`}
                      </Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testCategory) ? (
                  <Datalist.Cell label={columnLabels.testCategory}>
                    {TestSession.type}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testSessionName) ? (
                  <Datalist.Cell label={columnLabels.testSessionName}>
                    {TestSession.name}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
